import React, {useCallback} from 'react';
import {HStack, IconButton, Text, useColorMode, VStack} from '@tempo/core';
import {MoonOutlinedIcon, SunHalfIcon, SunOutlinedIcon} from '@tempo/icons';

import useUserSetting from '@deezer/next/shared/hooks/useUserSetting';
import type {ThemePreference} from '@deezer/next/services/storage/shared/types';
import {setThemePreference} from '@deezer/next/services/storage/shared/effects';
import Popover, {
	PopoverBody,
	PopoverContent,
	PopoverTrigger,
} from '@components/Popover';

type ThemeOptionValue = Extract<ThemePreference, 'light' | 'dark'>;

type ThemeCardProps = {
	label: string;
	value: ThemeOptionValue;
	isSelected: boolean;
	onSelect: (value: ThemeOptionValue) => void;
};

const ThemeCard = ({label, value, isSelected, onSelect}: ThemeCardProps) => {
	const Icon = value === 'dark' ? MoonOutlinedIcon : SunOutlinedIcon;

	return (
		<VStack
			spacing="spacing.m"
			py="spacing.l"
			px="spacing.2xl"
			align="center"
			justify="center"
			as="button"
			type="button"
			onClick={() => onSelect(value)}
			borderRadius="s"
			borderWidth={1}
			borderColor={
				isSelected
					? 'border.accent.primary.default'
					: 'border.neutral.primary.default'
			}
			bg={
				isSelected
					? 'background.neutral.primary.default'
					: 'background.neutral.secondary.default'
			}
			cursor="pointer"
		>
			<Icon
				size="medium"
				color={
					isSelected
						? 'icon.neutral.primary.selected'
						: 'icon.neutral.primary.default'
				}
			/>

			<Text
				variant="body.default.regular"
				color={
					isSelected
						? 'text.neutral.primary.selected'
						: 'text.neutral.secondary.default'
				}
			>
				{label}
			</Text>
		</VStack>
	);
};

const UserThemeToggle = () => {
	const {colorMode, setColorMode} = useColorMode();
	const {setSetting} = useUserSetting('user', 'theme');

	const selectedTheme: ThemeOptionValue =
		colorMode === 'dark' ? 'dark' : 'light';

	const handleThemeChange = useCallback(
		async (nextMode: ThemeOptionValue) => {
			if (nextMode === selectedTheme) {
				return;
			}

			setThemePreference(nextMode);
			setColorMode(nextMode);
			await setSetting({key: 'default', mode: nextMode});
		},
		[selectedTheme, setColorMode, setSetting],
	);

	return (
		<Popover placement="left-start">
			<PopoverTrigger>
				<IconButton
					size="medium"
					aria-label="open-theme-menu"
					variant="outline"
					icon={<SunHalfIcon />}
				/>
			</PopoverTrigger>

			<PopoverContent w="250px">
				<PopoverBody>
					<VStack spacing="spacing.l" align="stretch">
						<Text variant="title.section" color="text.neutral.primary.default">
							Theme
						</Text>

						<HStack spacing="spacing.l">
							<ThemeCard
								label="Dark"
								value="dark"
								isSelected={selectedTheme === 'dark'}
								onSelect={handleThemeChange}
							/>
							<ThemeCard
								label="Light"
								value="light"
								isSelected={selectedTheme === 'light'}
								onSelect={handleThemeChange}
							/>
						</HStack>
					</VStack>
				</PopoverBody>
			</PopoverContent>
		</Popover>
	);
};

export default UserThemeToggle;
